import { normalizeProject, normalizeProjects, SCHEMA_VERSION } from './project';
import type { Project } from './types';

/**
 * localStorage 仓储。
 *
 * 所有读写都走一个可注入的 `StorageLike`，好处有二：
 *   1. 隐私模式 / 禁用存储时能够优雅降级（返回空列表而不是抛错）；
 *   2. 单测可以直接塞一个内存实现，不需要 jsdom。
 */

export const PROJECTS_KEY = 'havorix.assess.projects.v1';
export const UI_KEY = 'havorix.assess.ui.v1';
export const EXPORT_APP_ID = 'havorix-assess';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface UiState {
  lastProjectId: string | null;
}

export function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    // 触发一次读写，确认存储真的可用（Safari 隐私模式下 setItem 会抛错）
    const probe = '__havorix_assess_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

function readJson(storage: StorageLike | null, key: string): unknown {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    // 数据被外部破坏时不阻塞使用，直接当作空数据
    return null;
  }
}

function writeJson(storage: StorageLike | null, key: string, value: unknown): boolean {
  if (!storage) return false;
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadProjects(storage: StorageLike | null = defaultStorage()): Project[] {
  const raw = readJson(storage, PROJECTS_KEY);
  if (!raw || typeof raw !== 'object') return [];
  return normalizeProjects((raw as { projects?: unknown }).projects);
}

export function saveProjects(projects: Project[], storage: StorageLike | null = defaultStorage()): boolean {
  return writeJson(storage, PROJECTS_KEY, { version: SCHEMA_VERSION, projects });
}

export function loadUiState(storage: StorageLike | null = defaultStorage()): UiState {
  const raw = readJson(storage, UI_KEY);
  if (!raw || typeof raw !== 'object') return { lastProjectId: null };
  const lastProjectId = (raw as { lastProjectId?: unknown }).lastProjectId;
  return { lastProjectId: typeof lastProjectId === 'string' ? lastProjectId : null };
}

export function saveUiState(state: UiState, storage: StorageLike | null = defaultStorage()): boolean {
  return writeJson(storage, UI_KEY, state);
}

/* ------------------------------------------------------------------ *
 * 导入 / 导出
 * ------------------------------------------------------------------ */

export interface ExportPayload {
  app: typeof EXPORT_APP_ID;
  version: number;
  exportedAt: string;
  project: Project;
}

export function buildExportPayload(project: Project): ExportPayload {
  return {
    app: EXPORT_APP_ID,
    version: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    project,
  };
}

export function exportFileName(project: Project): string {
  const safe = project.name.replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safe || 'havorix-assess'}.json`;
}

export type ImportResult = { ok: true; project: Project } | { ok: false; error: string };

/**
 * 解析导入的项目文件。既接受本系统导出的完整载荷，也接受裸项目对象，
 * 清洗后仍无法识别时给出可读的中文错误。
 */
export function parseImportPayload(text: string): ImportResult {
  if (!text.trim()) return { ok: false, error: '文件内容为空。' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, error: '不是合法的 JSON 文件。' };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, error: '文件结构不正确，缺少项目数据。' };
  }

  const source = parsed as Record<string, unknown>;
  const candidate = 'project' in source ? source.project : source;
  const project = normalizeProject(candidate);

  if (!project || project.components.length === 0) {
    return { ok: false, error: '未能在文件中找到有效的项目数据。' };
  }

  return { ok: true, project };
}
